import { fileUrl } from "@/lib/images";

const Meta = ({ label, value }) =>
  value ? (
    <div className="print-avoid">
      <p className="text-[0.65rem] uppercase tracking-[0.18em] text-[#8C6E48] mb-1">{label}</p>
      <p className="text-sm leading-relaxed text-[#1A1A1A]">{value}</p>
    </div>
  ) : null;

const Step = ({ s, n, token }) => (
  <li className="grid md:grid-cols-2 gap-6 items-center py-6 border-b border-[#E5E0D8] print-avoid" data-testid={`book-step-${s.code}`}>
    <div className="md:order-2">
      {s.image_path ? (
        <a href={fileUrl(s.image_path, token)} target="_blank" rel="noreferrer">
          <img src={fileUrl(s.image_path, token)} alt={s.alt || s.title} className="w-full aspect-[3/2] object-cover hover:opacity-90 transition-opacity" />
        </a>
      ) : (
        <div className="w-full aspect-[3/2] bg-[#F0EBE1] flex items-center justify-center text-xs text-[#8C6E48] tracking-[0.15em] uppercase">Bilde kommer · {s.code}</div>
      )}
      <p className="text-[0.65rem] text-[#5C5A56] mt-2">{s.code}</p>
    </div>
    <div>
      <p className="font-serif-display text-4xl text-[#C4A47C]">{String(n).padStart(2, "0")}</p>
      <h4 className="text-2xl font-medium mt-1">{s.title}</h4>
      <p className="mt-3 text-[#5C5A56] leading-relaxed">{s.text}</p>
    </div>
  </li>
);

export const Task = ({ t, token }) => (
  <article className="mt-12 print-break" data-testid={`book-task-${t.id}`}>
    <h3 className="text-3xl md:text-4xl font-medium tracking-tight">{t.title}</h3>
    {t.goal && <p className="mt-2 text-[#5C5A56]">{t.goal}</p>}
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6 bg-[#F0EBE1]">
      <Meta label="Vanskelighetsgrad" value={t.difficulty} />
      <Meta label="Tid" value={t.time} />
      <Meta label="Passer for" value={t.suits} />
      <Meta label="Dette trenger du" value={t.tools} />
    </div>
    {t.steps?.length > 0 && <ol className="mt-4">{t.steps.map((s, i) => <Step key={s.code} s={s} n={i + 1} token={token} />)}</ol>}
    {t.locked_steps > 0 && (
      <p className="mt-6 p-5 border border-dashed border-[#C4A47C] text-sm text-[#8C6E48]" data-testid={`book-task-locked-steps-${t.id}`}>
        + {t.locked_steps} steg til, vanlige feil og kontroll av resultatet finnes i den fulle håndboken.
      </p>
    )}
    <div className="mt-8 grid md:grid-cols-3 gap-6">
      <Meta label="Vanlige feil" value={t.mistakes} />
      <Meta label="Kontroller resultatet" value={t.check} />
      <Meta label="Når bør du gå til skredder?" value={t.when_pro} />
    </div>
  </article>
);

const LockedTask = ({ t }) => (
  <li className="flex gap-3 text-sm py-1"><span className="text-[#C4A47C]">—</span>{t.title}<span className="text-[#8C6E48] italic">· kun for kjøpere</span></li>
);

export const Chapter = ({ c, token }) => (
  <section id={c.id} className="py-16 border-t border-[#E5E0D8] scroll-mt-24 print-break" data-testid={`book-chapter-${c.number}`}>
    <p className="eyebrow">Kapittel {c.number}</p>
    <h2 className="text-4xl md:text-5xl font-medium tracking-tight mt-3 max-w-3xl">{c.title}</h2>
    <div className="grid lg:grid-cols-2 gap-10 mt-8 items-start">
      <p className="text-base md:text-lg text-[#5C5A56] leading-relaxed">{c.intro}</p>
      {c.image_path && <img src={fileUrl(c.image_path, token)} alt={c.title} className="w-full aspect-[3/2] object-cover" />}
    </div>
    {c.tasks.filter((t) => !t.locked).map((t) => <Task key={t.id} t={t} token={token} />)}
    {c.tasks.some((t) => t.locked) && (
      <ul className="mt-8" data-testid={`book-chapter-locked-${c.number}`}>{c.tasks.filter((t) => t.locked).map((t) => <LockedTask key={t.id} t={t} />)}</ul>
    )}
  </section>
);

export const Toc = ({ chapters }) => (
  <ol className="space-y-3" data-testid="book-toc">
    {chapters.map((c) => (
      <li key={c.id}>
        <a href={`#${c.id}`} className="flex gap-4 items-baseline hover:text-[#8C6E48] transition-colors">
          <span className="font-serif-display text-2xl text-[#C4A47C] w-8">{c.number}</span>
          <span>{c.title}{c.tasks.some((t) => t.locked_steps !== undefined) && <span className="ml-2 text-xs text-[#8C6E48] uppercase tracking-[0.15em]">Smakebit</span>}</span>
        </a>
      </li>
    ))}
  </ol>
);
